// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Mail page's folder panel on desktop (design/mail-folders). One panel, always in the
// same spot at the left of the mail list: it slides out over the list from the folder button and
// goes away once a folder is picked, or it is pinned there and keeps its room. Pinned or not is
// kept on this device; until the person chooses, it is pinned on windows wide enough for the
// panel, the list and the reader side by side.

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import Window from '@app/features/window/state/Window';
import {makeAutoObservable} from 'mobx';

/** Narrower than this, the panel starts unpinned (the reader would get too tight). */
export const MAIL_FOLDER_PANE_AUTO_WIDTH = 1400;

class MailFolderPane {
	/** The person's choice on this device; null until they pin or unpin it. */
	pinnedValue: boolean | null = null;
	/** Folders whose children are folded away, by id. */
	folded: Array<string> = [];
	/** Unpinned: the panel is out over the list right now. */
	drawerOpen = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void makePersistent(this, 'MailFolderPanel', ['pinnedValue', 'folded'], {syncAcrossTabs: true});
	}

	get pinned(): boolean {
		return this.pinnedValue ?? Window.windowSize.width >= MAIL_FOLDER_PANE_AUTO_WIDTH;
	}

	setPinned(pinned: boolean): void {
		this.pinnedValue = pinned;
		this.drawerOpen = false;
	}

	openDrawer(): void {
		if (!this.pinned) this.drawerOpen = true;
	}

	closeDrawer(): void {
		this.drawerOpen = false;
	}

	toggleDrawer(): void {
		if (this.drawerOpen) this.closeDrawer();
		else this.openDrawer();
	}

	isFolded(folderId: string): boolean {
		return this.folded.includes(folderId);
	}

	toggleFolded(folderId: string): void {
		this.folded = this.isFolded(folderId) ? this.folded.filter((id) => id !== folderId) : [...this.folded, folderId];
	}
}

export default new MailFolderPane();
